import { useState } from 'react';
import ProStudioAgentSection from './ProStudioAgentSection';
import ProStudioHookSection from './ProStudioHookSection';

// ============================================================
// ★ MOCKUP UI SAHAJA — "PRO STUDIO" (page penuh)
// Rumah pusat semua setting custom user (pakej Premium Pro /
// Fighter Pro): Gaya (per module, per set) + Hook Bank + CTA &
// Hashtag + Prompt Saya + Template Saya (4 terakhir placeholder).
// Tiada logic sebenar: TIADA Firestore, TIADA localStorage,
// TIADA kesan pada Generate. State lokal je (hilang bila reload).
//
// ★ POSTUR PAPARAN (keputusan user 2026-07-26 — "declare siap2"):
//   Route /pro-studio kini TERBUKA kepada SEMUA user (bukan admin-only)
//   supaya SELAMAT walaupun ter-deploy tanpa sengaja. Non-admin:
//     • nampak SEMUA tab bertanda SOON,
//     • boleh terokai rangka dalaman (browse je),
//     • TAK boleh edit — kandungan dibalut <fieldset disabled> yang
//       melumpuhkan semua input/select/textarea/button di dalamnya.
//   Tujuan: warm-up / awareness ada kemas kini besar akan datang.
//   Admin (readOnly=false) kekal boleh edit penuh untuk terus membina.
//
// Extra features dalam mockup (diluluskan user 2026-07-23):
//   1. Shuffle (Mod Rawak) — per field, tanda banyak pilihan,
//      sistem rawak kombinasi setiap Generate (kos API sifar,
//      rawak jadi di client sebelum selit ke prompt).
//   2. Default Peribadi — bintang satu template, auto terpilih
//      setiap kali buka workspace.
// ============================================================

const AUTO = 'Auto (AI tentukan)';

const DROPDOWNS = [
  { key: 'sudut',    label: 'Sudut Kamera',   options: [AUTO, 'High Angle', 'Eye Level', 'Low Angle'] },
  { key: 'lokasi',   label: 'Lokasi / Latar', options: [AUTO, 'Bilik Moden', 'Dapur', 'Ruang Tamu', 'Outdoor', 'Kedai / Butik'] },
  { key: 'ekspresi', label: 'Ekspresi Model', options: [AUTO, 'Senyum Lembut', 'Ceria', 'Serius / Yakin'] },
  { key: 'cahaya',   label: 'Pencahayaan',    options: [AUTO, 'Cahaya Lembut', 'Rim Light', 'Warm Tone'] },
];

const EMPTY_SET = { sudut: AUTO, lokasi: AUTO, ekspresi: AUTO, cahaya: AUTO, bebas: '' };
const EMPTY_SHUFFLE_SET = { sudut: { on: false, picks: [] }, lokasi: { on: false, picks: [] }, ekspresi: { on: false, picks: [] }, cahaya: { on: false, picks: [] } };
const BUCKETS = ['all', 's1', 's2', 's3'];
const emptyValues = () => ({ all: { ...EMPTY_SET }, s1: { ...EMPTY_SET }, s2: { ...EMPTY_SET }, s3: { ...EMPTY_SET } });
const emptyShuffle = () => JSON.parse(JSON.stringify({ all: EMPTY_SHUFFLE_SET, s1: EMPTY_SHUFFLE_SET, s2: EMPTY_SHUFFLE_SET, s3: EMPTY_SHUFFLE_SET }));
const deepCopy = (o) => JSON.parse(JSON.stringify(o));

const SET_META = {
  all: { title: 'SEMUA SET', sub: 'Set 1 + 2 + 3 guna setting sama' },
  s1:  { title: 'SET 1', sub: 'Angle: Masalah' },
  s2:  { title: 'SET 2', sub: 'Angle: Hasil / Manfaat' },
  s3:  { title: 'SET 3', sub: 'Angle: Curiosity' },
};

// ★ SEMUA tab bertanda SOON (ready:false) — keputusan user 2026-07-26.
// `ready` HANYA kawal lencana SOON, BUKAN sama ada seksyen itu render.
// Gaya / Agent Saya / Hook Bank tetap papar rangka penuh; tiga lagi papar placeholder.
const SECTIONS = [
  { id: 'gaya',      icon: 'palette',       label: 'Gaya',          ready: false },
  // Agent Saya — sambung custom GPT milik pelanggan sendiri (segmen "beli GPT dari seller lain")
  { id: 'agent',     icon: 'smart_toy',     label: 'Agent Saya',    ready: true },
  // Hook Bank — group hook milik user, dikongsi SEMUA module (bukan per-module macam Gaya)
  { id: 'hook',      icon: 'anchor',        label: 'Hook Bank',     ready: false },
  { id: 'cta',       icon: 'tag',           label: 'CTA & Hashtag', ready: false },
  { id: 'prompt',    icon: 'edit_note',     label: 'Prompt Saya',   ready: false },
  { id: 'templates', icon: 'folder_special', label: 'Template Saya', ready: false },
];

const MODULES = [
  { id: 'ai8saat',     name: 'AI8SAAT',      ready: true },
  { id: 'ai16saat',    name: 'AI16SAAT',     ready: false },
  { id: 'homeniche',   name: 'Home Niche',   ready: false },
  { id: 'goyang',      name: 'GOYANG',       ready: false },
  { id: 'smartcustom', name: 'Smart Custom', ready: false },
  { id: 'flownity',    name: 'FLOWNITY',     ready: false },
];

const MAX_TEMPLATES = 5; // nanti: ikut lesen (trial 1, paid default 5) — mekanik sama maxProfiles avatar

// Demo data — nanti dari Firestore users/{uid}
const initialTemplates = () => {
  const t1 = {
    name: 'Supplement Style', mode: 'perset', values: emptyValues(), shuffle: emptyShuffle(),
  };
  t1.values.s1 = { sudut: 'High Angle', lokasi: 'Bilik Moden',   ekspresi: 'Serius / Yakin', cahaya: 'Cahaya Lembut', bebas: '' };
  t1.values.s2 = { sudut: 'Eye Level',  lokasi: 'Outdoor',       ekspresi: 'Ceria',          cahaya: 'Warm Tone',     bebas: '' };
  t1.values.s3 = { sudut: 'Low Angle',  lokasi: 'Kedai / Butik', ekspresi: 'Senyum Lembut',  cahaya: 'Rim Light',     bebas: '' };
  // Demo Shuffle: Set 1 lokasi dirawak antara 2 pilihan
  t1.shuffle.s1.lokasi = { on: true, picks: ['Bilik Moden', 'Ruang Tamu'] };

  const t2 = {
    name: 'Produk Dapur', mode: 'all', values: emptyValues(), shuffle: emptyShuffle(),
  };
  t2.values.all = { sudut: 'Eye Level', lokasi: 'Dapur', ekspresi: 'Ceria', cahaya: 'Cahaya Lembut', bebas: '' };
  return [t1, t2];
};

/* `aksesSmart` / `sebabKunci` — dikira di App.jsx dengan penjaga lesen
   yang SAMA seperti Rak Formula (`computeFormulaAccess`). Seksyen
   Agent Saya sahaja yang bergantung padanya; lima seksyen lain
   merentas semua module, jadi ia kekal di bawah `readOnly` biasa. */
export default function ProStudioView({ readOnly = false, aksesSmart = true, onNaikTaraf }) {
  const [activeSection, setActiveSection] = useState('gaya');
  const [activeModule, setActiveModule] = useState('ai8saat');
  const [templates, setTemplates] = useState(initialTemplates);
  const [defaultTpl, setDefaultTpl] = useState('Supplement Style'); // ⭐ Default Peribadi
  const [selectedTpl, setSelectedTpl] = useState('Supplement Style');
  const [mode, setMode] = useState('perset');
  const [values, setValues] = useState(() => deepCopy(initialTemplates()[0].values));
  const [shuffle, setShuffle] = useState(() => deepCopy(initialTemplates()[0].shuffle));
  const [savedFlash, setSavedFlash] = useState(false);

  const quotaFull = templates.length >= MAX_TEMPLATES;
  const buckets = mode === 'all' ? ['all'] : ['s1', 's2', 's3'];

  const loadTemplate = (t) => {
    setSelectedTpl(t.name);
    setMode(t.mode);
    setValues(deepCopy(t.values));
    setShuffle(deepCopy(t.shuffle));
  };

  const newTemplate = () => {
    if (quotaFull) return;
    let n = 1;
    while (templates.some(t => t.name === `Template Baru ${n}`)) n++;
    const t = { name: `Template Baru ${n}`, mode: 'all', values: emptyValues(), shuffle: emptyShuffle() };
    setTemplates(prev => [...prev, t]);
    loadTemplate(t);
  };

  const saveCurrent = () => {
    if (!selectedTpl) return;
    setTemplates(prev => prev.map(t => t.name === selectedTpl
      ? { ...t, mode, values: deepCopy(values), shuffle: deepCopy(shuffle) }
      : t));
    setSavedFlash(true);
    setTimeout(() => setSavedFlash(false), 1500);
  };

  const deleteTemplate = (name) => {
    setTemplates(prev => prev.filter(t => t.name !== name));
    if (defaultTpl === name) setDefaultTpl('');
    if (selectedTpl === name) {
      setSelectedTpl('');
      setValues(emptyValues());
      setShuffle(emptyShuffle());
      setMode('all');
    }
  };

  const setValue = (bucket, key, val) =>
    setValues(prev => ({ ...prev, [bucket]: { ...prev[bucket], [key]: val } }));

  const toggleShuffleOn = (bucket, key) =>
    setShuffle(prev => {
      const cur = prev[bucket][key];
      return { ...prev, [bucket]: { ...prev[bucket], [key]: { ...cur, on: !cur.on } } };
    });

  const toggleShufflePick = (bucket, key, opt) =>
    setShuffle(prev => {
      const cur = prev[bucket][key];
      const picks = cur.picks.includes(opt) ? cur.picks.filter(p => p !== opt) : [...cur.picks, opt];
      return { ...prev, [bucket]: { ...prev[bucket], [key]: { ...cur, picks } } };
    });

  const copyBucketToOthers = (src) => {
    setValues(prev => ({ ...prev, s1: deepCopy(prev[src]), s2: deepCopy(prev[src]), s3: deepCopy(prev[src]) }));
    setShuffle(prev => ({ ...prev, s1: deepCopy(prev[src]), s2: deepCopy(prev[src]), s3: deepCopy(prev[src]) }));
  };

  const switchMode = (m) => { if (m !== mode) setMode(m); };

  // ── Satu field (dropdown ATAU chips shuffle) ──
  const renderField = (bucket, dd) => {
    const sh = shuffle[bucket][dd.key];
    return (
      <div key={dd.key}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
          <span style={{ fontSize: 10, fontWeight: 700, color: '#64748b' }}>{dd.label}</span>
          {/* 🎲 Suis Rawak per field — tanda banyak pilihan, sistem rawak setiap Generate */}
          <button
            type="button"
            onClick={() => toggleShuffleOn(bucket, dd.key)}
            title={sh.on ? 'Tutup Mod Rawak — kembali pilih satu nilai' : 'Mod Rawak: tanda beberapa pilihan, sistem rawak setiap Generate'}
            style={{
              display: 'flex', alignItems: 'center', gap: 3, cursor: 'pointer',
              fontSize: 8.5, fontWeight: 800, letterSpacing: '0.04em',
              padding: '2px 7px', borderRadius: 999,
              background: sh.on ? '#6366f1' : '#f8fafc',
              border: `1px solid ${sh.on ? '#6366f1' : '#e2e8f0'}`,
              color: sh.on ? '#ffffff' : '#94a3b8',
              transition: 'all 0.15s',
            }}
          >
            <span className="material-symbols-outlined" style={{ fontSize: 11 }}>casino</span>
            RAWAK
          </button>
        </div>

        {!sh.on && (
          <select
            value={values[bucket][dd.key]}
            onChange={(e) => setValue(bucket, dd.key, e.target.value)}
            style={{
              width: '100%', padding: '7px 8px', fontSize: 11, fontWeight: 600,
              background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: 8,
              color: values[bucket][dd.key] === AUTO ? '#94a3b8' : '#1e293b',
              outline: 'none', cursor: 'pointer', boxSizing: 'border-box',
            }}
          >
            {dd.options.map(o => <option key={o} value={o}>{o}</option>)}
          </select>
        )}

        {sh.on && (
          <div style={{
            display: 'flex', flexWrap: 'wrap', gap: 4, padding: 6,
            background: '#f5f3ff', border: '1px dashed #c7d2fe', borderRadius: 8,
          }}>
            {dd.options.filter(o => o !== AUTO).map(o => {
              const picked = sh.picks.includes(o);
              return (
                <button
                  key={o}
                  type="button"
                  onClick={() => toggleShufflePick(bucket, dd.key, o)}
                  style={{
                    fontSize: 9.5, fontWeight: 700, padding: '3px 8px', borderRadius: 999,
                    cursor: 'pointer', transition: 'all 0.12s',
                    background: picked ? '#6366f1' : '#ffffff',
                    border: `1px solid ${picked ? '#6366f1' : '#e2e8f0'}`,
                    color: picked ? '#ffffff' : '#64748b',
                  }}
                >{o}</button>
              );
            })}
            <div style={{ width: '100%', fontSize: 8.5, fontWeight: 600, color: '#8b5cf6', marginTop: 2 }}>
              {sh.picks.length === 0
                ? 'Tanda sekurang-kurangnya 1 pilihan untuk dirawak'
                : `🎲 Setiap Generate: rawak antara ${sh.picks.length} pilihan`}
            </div>
          </div>
        )}
      </div>
    );
  };

  // ── Kolum satu set ──
  const renderSetColumn = (bucket) => (
    <div key={bucket} style={{
      background: '#ffffff', border: '1px solid #e8edf4', borderRadius: 12, padding: 12,
      display: 'flex', flexDirection: 'column', gap: 10, minWidth: 0,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingBottom: 8, borderBottom: '1px solid #f1f5f9' }}>
        <div>
          <div style={{ fontSize: 11, fontWeight: 800, color: '#4338ca', letterSpacing: '0.05em' }}>{SET_META[bucket].title}</div>
          <div style={{ fontSize: 9, color: '#94a3b8', fontWeight: 600 }}>{SET_META[bucket].sub}</div>
        </div>
        {bucket !== 'all' && (
          <button
            type="button"
            onClick={() => copyBucketToOthers(bucket)}
            title="Salin setting set ini ke semua set lain"
            style={{
              width: 24, height: 24, borderRadius: 6, cursor: 'pointer', padding: 0,
              background: '#f8fafc', border: '1px solid #e2e8f0', color: '#6366f1',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
          >
            <span className="material-symbols-outlined" style={{ fontSize: 14 }}>content_copy</span>
          </button>
        )}
      </div>

      {DROPDOWNS.map(dd => renderField(bucket, dd))}

      <div>
        <div style={{ fontSize: 10, fontWeight: 700, color: '#64748b', marginBottom: 4 }}>Arahan Tambahan (pilihan)</div>
        <textarea
          value={values[bucket].bebas}
          onChange={(e) => setValue(bucket, 'bebas', e.target.value)}
          placeholder="Contoh: 'suasana pagi yang tenang'"
          style={{
            width: '100%', minHeight: 44, resize: 'vertical', padding: '7px 8px',
            fontSize: 11, fontWeight: 500, fontFamily: 'inherit', lineHeight: 1.5,
            background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: 8,
            color: '#1e293b', outline: 'none', boxSizing: 'border-box',
          }}
        />
      </div>
    </div>
  );

  return (
    <div style={{ flex: 1, minWidth: 0, height: '100vh', overflowY: 'auto', background: '#f5f6fa' }}>
      <div className="sesaat-ps-page" style={{ maxWidth: 1280, margin: '0 auto', padding: '20px 24px 60px' }}>

        {/* ═══ Header page ═══ */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4, flexWrap: 'wrap' }}>
          <span className="material-symbols-outlined" style={{
            fontSize: 26, color: '#ffffff', background: 'linear-gradient(135deg,#6366f1,#8b5cf6)',
            borderRadius: 10, padding: 6,
          }}>bolt</span>
          <h1 style={{ fontSize: 22, fontWeight: 900, color: '#0f172a', margin: 0, letterSpacing: '-0.02em' }}>PRO Studio</h1>
          <span style={{
            fontSize: 9, fontWeight: 900, letterSpacing: '0.08em',
            color: readOnly ? '#0891b2' : '#d97706',
            background: readOnly ? '#ecfeff' : '#fffbeb',
            border: `1px solid ${readOnly ? '#a5f3fc' : '#fde68a'}`,
            padding: '2px 8px', borderRadius: 999,
          }}>{readOnly ? 'AKAN DATANG' : 'MOCKUP'}</span>
          {/* ⛔ Lencana "PREMIUM PRO · FIGHTER PRO" DIBUANG 2026-08-17
              (arahan user). Dua pakej itu tidak wujud dalam sistem lesen —
              yang ada hanya Premium, Fighter dan Custom. Dibuang serentak
              dengan lencana yang sama pada rupa v3.9. */}
        </div>
        <p style={{ fontSize: 12.5, color: '#64748b', margin: '0 0 18px', maxWidth: 640 }}>
          Tempat anda simpan hook, gaya dan arahan sendiri.
          Apa yang anda set di sini akan digunakan setiap kali anda tekan Generate di Workspace.
        </p>

        {/* ═══ Sepanduk pratonton — non-admin sahaja (warm-up/awareness) ═══ */}
        {readOnly && (
          <div style={{
            display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 18,
            background: 'linear-gradient(135deg,#ecfeff 0%,#eef2ff 100%)',
            border: '1px solid #a5f3fc', borderRadius: 12, padding: '12px 16px', maxWidth: 760,
          }}>
            <span className="material-symbols-outlined" style={{ fontSize: 20, color: '#0891b2', flexShrink: 0 }}>visibility</span>
            <div style={{ fontSize: 12, color: '#155e75', fontWeight: 600, lineHeight: 1.6 }}>
              <b>Belum dibuka — anda boleh tengok dahulu.</b> Ini tempat anda akan simpan hook,
              gaya dan arahan sendiri nanti.
              <div style={{ marginTop: 4, fontWeight: 500, color: '#0e7490' }}>
                Klik mana-mana bahagian di sebelah kiri untuk lihat apa yang akan datang.
                Semua tetapan belum boleh diubah buat masa ini.
              </div>
            </div>
          </div>
        )}

        <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start', flexWrap: 'wrap' }}>

          {/* ═══ Nav seksyen (kiri) ═══ */}
          {/* Pada telefon ia bertukar jadi baris mendatar yang boleh di-scroll
              (lihat .sesaat-ps-nav dalam mobile.css) — lajur 190px di sebelah
              kiri akan makan separuh lebar skrin. */}
          <div className="sesaat-ps-nav" style={{ flex: '0 0 190px', display: 'flex', flexDirection: 'column', gap: 4 }}>
            {SECTIONS.map(s => {
              const active = activeSection === s.id;
              return (
                <button
                  key={s.id}
                  type="button"
                  onClick={() => setActiveSection(s.id)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 8, padding: '9px 12px',
                    borderRadius: 10, cursor: 'pointer', textAlign: 'left',
                    background: active ? '#ffffff' : 'transparent',
                    border: `1px solid ${active ? '#c7d2fe' : 'transparent'}`,
                    boxShadow: active ? '0 1px 3px rgba(99,102,241,0.12)' : 'none',
                    transition: 'all 0.15s',
                  }}
                >
                  <span className="material-symbols-outlined" style={{ fontSize: 17, color: active ? '#6366f1' : '#94a3b8' }}>{s.icon}</span>
                  <span style={{ fontSize: 12, fontWeight: active ? 800 : 600, color: active ? '#1e293b' : '#64748b', flex: 1 }}>{s.label}</span>
                  {!s.ready && (
                    <span style={{
                      fontSize: 7.5, fontWeight: 900, letterSpacing: '0.06em', color: '#94a3b8',
                      background: '#f1f5f9', padding: '2px 5px', borderRadius: 4,
                    }}>SOON</span>
                  )}
                </button>
              );
            })}
          </div>

          {/* ═══ Kandungan (kanan) ═══ */}
          <div style={{ flex: '1 1 560px', minWidth: 0 }}>

            {/* ★ Kunci baca-sahaja (non-admin): <fieldset disabled> melumpuhkan SEMUA
                input/select/textarea/button di dalamnya sekali gus — rangka kekal nampak.
                Nav seksyen sengaja DI LUAR fieldset supaya tab masih boleh ditukar. */}
            {/* ⭐ Seksyen Agent Saya TIDAK lagi tunduk kepada `readOnly`
                (arahan user 2026-08-16): module SMART dilepaskan kepada
                pelanggan, jadi pelanggan yang ADA SMART mesti boleh
                menambah agent sendiri. Yang tiada SMART nampak skrin
                berkunci — dikendalikan di dalam seksyen itu sendiri.
                Lima seksyen lain kekal baca-sahaja seperti sebelum ini. */}
            <fieldset
                /* ⛔ Seksyen Agent TIDAK pernah dilumpuhkan di sini.
                    Tiada akses → seksyen itu memapar skrin berkunci sendiri,
                    dan melumpuhkannya akan mematikan butang "Lihat pakej"
                    pada skrin itu juga (aduan user 2026-08-17).
                    Ada akses → pelanggan memang patut boleh edit. */
                disabled={activeSection === 'agent' ? false : readOnly}
                style={{ border: 'none', padding: 0, margin: 0, minWidth: 0 }}
            >

            {/* ── Seksyen AGENT SAYA — sambung custom GPT pelanggan ── */}
            {activeSection === 'agent' && (
                <ProStudioAgentSection
                    aksesSmart={aksesSmart}
                    onNaikTaraf={onNaikTaraf}
                />
            )}

            {/* ── Seksyen HOOK BANK (mockup — group hook user, kongsi semua module) ── */}
            {activeSection === 'hook' && <ProStudioHookSection />}

            {/* ── Seksyen placeholder (belum siap) ── */}
            {activeSection !== 'gaya' && activeSection !== 'agent' && activeSection !== 'hook' && (
              <div style={{
                background: '#ffffff', border: '1px dashed #e2e8f0', borderRadius: 14,
                padding: '48px 24px', textAlign: 'center',
              }}>
                <span className="material-symbols-outlined" style={{ fontSize: 40, color: '#cbd5e1' }}>
                  {SECTIONS.find(s => s.id === activeSection)?.icon}
                </span>
                <div style={{ fontSize: 15, fontWeight: 800, color: '#64748b', marginTop: 8 }}>
                  {SECTIONS.find(s => s.id === activeSection)?.label}
                </div>
                <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 4 }}>
                  Akan datang — bahagian ini akan dibina selepas seksyen Gaya stabil.
                </div>
              </div>
            )}

            {/* ── Seksyen GAYA ── */}
            {activeSection === 'gaya' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>

                {/* Pilih module */}
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  {MODULES.map(m => {
                    const active = activeModule === m.id;
                    return (
                      <button
                        key={m.id}
                        type="button"
                        disabled={!m.ready}
                        onClick={() => m.ready && setActiveModule(m.id)}
                        title={m.ready ? `Setting gaya ${m.name}` : `${m.name} — akan datang (AI8SAAT dulu, stabil baru roll)`}
                        style={{
                          padding: '7px 14px', borderRadius: 999, fontSize: 11, fontWeight: 800,
                          cursor: m.ready ? 'pointer' : 'not-allowed',
                          background: active ? '#6366f1' : '#ffffff',
                          border: `1.5px solid ${active ? '#6366f1' : '#e2e8f0'}`,
                          color: active ? '#ffffff' : m.ready ? '#334155' : '#cbd5e1',
                          display: 'flex', alignItems: 'center', gap: 5,
                        }}
                      >
                        {!m.ready && <span className="material-symbols-outlined" style={{ fontSize: 12 }}>lock</span>}
                        {m.name}
                      </button>
                    );
                  })}
                </div>

                <div style={{ display: 'flex', gap: 14, alignItems: 'flex-start', flexWrap: 'wrap' }}>

                  {/* ── Senarai template (tengah) ── */}
                  <div style={{
                    flex: '0 0 225px', background: '#ffffff', border: '1px solid #e8edf4',
                    borderRadius: 12, padding: 12, display: 'flex', flexDirection: 'column', gap: 8,
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <span style={{ fontSize: 10, fontWeight: 800, color: '#64748b', letterSpacing: '0.05em', textTransform: 'uppercase' }}>Template Anda</span>
                      <span style={{
                        fontSize: 9, fontWeight: 800, padding: '2px 6px', borderRadius: 4,
                        color: quotaFull ? '#ef4444' : '#6366f1',
                        background: quotaFull ? '#fef2f2' : '#f5f3ff',
                        border: `1px solid ${quotaFull ? '#fee2e2' : '#e0e7ff'}`,
                      }}>{templates.length}/{MAX_TEMPLATES}</span>
                    </div>

                    {templates.map(t => {
                      const active = selectedTpl === t.name;
                      const isDefault = defaultTpl === t.name;
                      return (
                        <div
                          key={t.name}
                          onClick={() => loadTemplate(t)}
                          style={{
                            display: 'flex', alignItems: 'center', gap: 6, padding: '8px 9px',
                            borderRadius: 9, cursor: 'pointer',
                            background: active ? '#eef2ff' : '#f8fafc',
                            border: `1.5px solid ${active ? '#6366f1' : '#f1f5f9'}`,
                            transition: 'all 0.15s',
                          }}
                        >
                          {/* ⭐ Default Peribadi — auto terpilih setiap buka workspace */}
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); setDefaultTpl(isDefault ? '' : t.name); }}
                            title={isDefault ? 'Default Peribadi anda — auto terpilih di Workspace' : 'Jadikan Default Peribadi (auto terpilih setiap kali buka Workspace)'}
                            style={{
                              background: 'none', border: 'none', cursor: 'pointer', padding: 0,
                              display: 'flex', alignItems: 'center',
                            }}
                          >
                            <span className="material-symbols-outlined" style={{
                              fontSize: 16, color: isDefault ? '#f59e0b' : '#cbd5e1',
                              fontVariationSettings: isDefault ? "'FILL' 1" : "'FILL' 0",
                            }}>star</span>
                          </button>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{
                              fontSize: 11, fontWeight: 700, color: active ? '#4338ca' : '#334155',
                              whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                            }}>{t.name}</div>
                            <div style={{ fontSize: 8.5, color: '#94a3b8', fontWeight: 600 }}>
                              {t.mode === 'perset' ? 'Ikut set (S1·S2·S3)' : 'Semua set sama'}{isDefault ? ' · ⭐ default' : ''}
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); deleteTemplate(t.name); }}
                            title="Padam template"
                            style={{
                              background: 'none', border: 'none', cursor: 'pointer', padding: 2,
                              color: '#cbd5e1', display: 'flex', alignItems: 'center',
                            }}
                          >
                            <span className="material-symbols-outlined" style={{ fontSize: 14 }}>delete</span>
                          </button>
                        </div>
                      );
                    })}

                    <button
                      type="button"
                      disabled={quotaFull}
                      onClick={newTemplate}
                      style={{
                        padding: '8px 10px', borderRadius: 9, fontSize: 11, fontWeight: 800,
                        cursor: quotaFull ? 'not-allowed' : 'pointer',
                        background: '#ffffff',
                        border: `1px dashed ${quotaFull ? '#e2e8f0' : '#c7d2fe'}`,
                        color: quotaFull ? '#cbd5e1' : '#6366f1',
                        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4,
                      }}
                    >
                      <span className="material-symbols-outlined" style={{ fontSize: 14 }}>add</span>
                      {quotaFull ? 'Slot Penuh' : 'Template Baru'}
                    </button>

                    <div style={{ fontSize: 9, color: '#94a3b8', lineHeight: 1.5, paddingTop: 4, borderTop: '1px solid #f1f5f9' }}>
                      ⭐ = Default Peribadi — template ini terpilih secara automatik setiap kali anda buka Workspace.
                    </div>
                  </div>

                  {/* ── Editor (kanan) ── */}
                  <div style={{ flex: '1 1 400px', minWidth: 0, display: 'flex', flexDirection: 'column', gap: 12 }}>

                    {/* Bar editor: nama template + mod + simpan */}
                    <div style={{
                      display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
                      background: '#ffffff', border: '1px solid #e8edf4', borderRadius: 12, padding: '10px 12px',
                    }}>
                      <span className="material-symbols-outlined" style={{ fontSize: 16, color: '#6366f1' }}>edit</span>
                      <span style={{ fontSize: 12.5, fontWeight: 800, color: '#1e293b' }}>
                        {selectedTpl || 'Tiada template dipilih'}
                      </span>

                      <div style={{ display: 'flex', gap: 4, marginLeft: 'auto' }}>
                        {[{ m: 'all', label: 'Semua Set' }, { m: 'perset', label: 'Ikut Set' }].map(({ m, label }) => (
                          <button
                            key={m}
                            type="button"
                            onClick={() => switchMode(m)}
                            style={{
                              padding: '6px 12px', borderRadius: 8, cursor: 'pointer',
                              fontSize: 10.5, fontWeight: 700,
                              background: mode === m ? '#eef2ff' : '#ffffff',
                              border: `1.5px solid ${mode === m ? '#6366f1' : '#e2e8f0'}`,
                              color: mode === m ? '#4338ca' : '#64748b',
                            }}
                          >{label}</button>
                        ))}
                      </div>

                      <button
                        type="button"
                        disabled={!selectedTpl}
                        onClick={saveCurrent}
                        style={{
                          padding: '7px 14px', borderRadius: 8, fontSize: 11, fontWeight: 800,
                          cursor: selectedTpl ? 'pointer' : 'not-allowed',
                          background: savedFlash ? '#10b981' : selectedTpl ? '#6366f1' : '#e2e8f0',
                          border: 'none', color: '#ffffff',
                          display: 'flex', alignItems: 'center', gap: 5, transition: 'background 0.2s',
                        }}
                      >
                        <span className="material-symbols-outlined" style={{ fontSize: 14 }}>{savedFlash ? 'check' : 'save'}</span>
                        {savedFlash ? 'Disimpan' : 'Simpan'}
                      </button>
                    </div>

                    {/* Kolum set — SEBELAH-MENYEBELAH (sebab utama page luas) */}
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: mode === 'all' ? 'minmax(260px, 420px)' : 'repeat(auto-fit, minmax(240px, 1fr))',
                      gap: 12,
                    }}>
                      {buckets.map(renderSetColumn)}
                    </div>

                    {/* Nota rule teras */}
                    <div style={{
                      fontSize: 10, color: '#92600a', background: '#fffbeb',
                      border: '1px dashed #f2d38b', borderRadius: 10, padding: '8px 12px', lineHeight: 1.5,
                    }}>
                      <b>Nota:</b> Peraturan teras SESAAT (tudung untuk produk wanita, produk ikut gambar rujukan, pose ikut saiz produk) sentiasa diutamakan — pilihan gaya tidak boleh melanggarnya.
                    </div>
                  </div>
                </div>
              </div>
            )}
            </fieldset>
          </div>
        </div>
      </div>
    </div>
  );
}
